import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer, Question } from '@/components';
import { login } from '@/services/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

let isRefreshing = false;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    // 从 localStorage 获取用户信息
    const token = localStorage.getItem('Authorization');
    if (token) {
      // 获取存储的登录参数中的用户名
      let username = 'Admin';
      let userid: string | undefined;
      try {
        const loginParamsStr = localStorage.getItem('loginParams');
        if (loginParamsStr) {
          const loginParams = JSON.parse(loginParamsStr);
          if (loginParams.account) {
            username = loginParams.account;
          }
        }

        const storedLoginId = localStorage.getItem('loginId');
        if (storedLoginId) {
          userid = storedLoginId;
        }
      } catch (error) {
        console.error('Failed to parse loginParams', error);
      }

      // 这里可以根据实际情况，从 localStorage 获取更多用户信息
      // 或者直接返回一个默认的用户对象，避免重复请求后端
      const user = {
        name: username,
        avatar:
          'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
        userid: userid,
        access: 'admin',
      };
      return user;
    }
    history.push(loginPath);
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      isDev && <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: isDev
    ? process.env.UMI_APP_API_URL || ''
    : 'http://admin.helpwawa.cn',
  ...errorConfig,
  requestInterceptors: [
    async (config: any) => {
      // 检查 Token 是否临近过期（5分钟内），且当前不是登录接口本身
      const url = config.url;
      if (url !== '/api/doLogin') {
        const tokenExpireTimeStr = localStorage.getItem('tokenExpireTime');
        if (tokenExpireTimeStr) {
          const expireTime = parseInt(tokenExpireTimeStr, 10);
          const now = Date.now();

          // 合并处理：如果 Token 过期 OR 临近过期（5分钟内），都进行阻塞式刷新
          // 这样可以确保当前请求一定使用最新的 Token，避免并发导致旧 Token 请求失败
          if (now > expireTime - 300000) {
            if (!isRefreshing) {
              isRefreshing = true;
              const loginParamsStr = localStorage.getItem('loginParams');
              if (loginParamsStr) {
                try {
                  const loginParams = JSON.parse(loginParamsStr);
                  // 阻塞式调用登录接口刷新 Token
                  const msg = await login(loginParams);
                  if (msg.code === '0' && msg.data?.tokenValue) {
                    console.log('Token refresh success');
                    localStorage.setItem('Authorization', msg.data.tokenValue);
                    if (msg.data.tokenTimeout) {
                      const newExpireTime =
                        Date.now() + msg.data.tokenTimeout * 1000;
                      localStorage.setItem(
                        'tokenExpireTime',
                        newExpireTime.toString(),
                      );
                    }
                  }
                } catch (err) {
                  console.error('Token refresh failed', err);
                  // 如果刷新失败，跳转登录页
                  history.push(loginPath);
                } finally {
                  isRefreshing = false;
                }
              }
            } else {
              // 如果正在刷新，等待刷新完成
              await new Promise((resolve) => {
                const check = () => {
                  if (!isRefreshing) resolve(true);
                  else setTimeout(check, 100);
                };
                check();
              });
            }
          }
        }
      }

      const token = localStorage.getItem('Authorization');
      if (token) {
        // 按照用户要求，组合方式：Bearer + token
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return { ...config };
    },
  ],
  responseInterceptors: [
    [
      (response: any) => {
        // 拦截响应数据，进行个性化处理
        const data = response.data;
        if (data && data.code === '401') {
          const loginParamsStr = localStorage.getItem('loginParams');
          if (loginParamsStr && !response.config.url.includes('/api/doLogin')) {
            try {
              const loginParams = JSON.parse(loginParamsStr);
              // 使用 Umi 的 request 需要异步，但这里我们返回一个 Promise
              return (async () => {
                try {
                  console.log('Detected 401, trying to re-login...');
                  const msg = await login(loginParams);
                  if (msg.code === '0' && msg.data?.tokenValue) {
                    console.log('Re-login success, retrying original request');
                    localStorage.setItem('Authorization', msg.data.tokenValue);
                    if (msg.data.tokenTimeout) {
                      const newExpireTime =
                        Date.now() + msg.data.tokenTimeout * 1000;
                      localStorage.setItem(
                        'tokenExpireTime',
                        newExpireTime.toString(),
                      );
                    }

                    // 更新原请求的 Token
                    const config = response.config;
                    config.headers.Authorization = `Bearer ${msg.data.tokenValue}`;

                    // 动态导入 request 以避免循环依赖
                    const { request: requestMethod } = await import(
                      '@umijs/max'
                    );
                    return requestMethod(config.url, config);
                  }
                } catch (e) {
                  console.error('Re-login failed', e);
                }
                return response; // 如果重试失败，返回原响应（让错误透传或显示）
              })();
            } catch (_e) {
              // ignore
            }
          }
        }
        return response;
      },
      (error: any) => {
        // 处理 HTTP 错误，如 401
        if (error.response && error.response.status === 401) {
          const loginParamsStr = localStorage.getItem('loginParams');
          if (loginParamsStr && !error.config.url.includes('/api/doLogin')) {
            return (async () => {
              try {
                const loginParams = JSON.parse(loginParamsStr);
                console.log('Detected HTTP 401, trying to re-login...');
                const msg = await login(loginParams);
                if (msg.code === '0' && msg.data?.tokenValue) {
                  localStorage.setItem('Authorization', msg.data.tokenValue);
                  if (msg.data.tokenTimeout) {
                    const newExpireTime =
                      Date.now() + msg.data.tokenTimeout * 1000;
                    localStorage.setItem(
                      'tokenExpireTime',
                      newExpireTime.toString(),
                    );
                  }
                  const config = error.config;
                  config.headers.Authorization = `Bearer ${msg.data.tokenValue}`;
                  const { request: requestMethod } = await import('@umijs/max');
                  return requestMethod(config.url, config);
                }
              } catch (e) {
                console.error('HTTP 401 Re-login failed', e);
              }
              return Promise.reject(error);
            })();
          }
        }
        return Promise.reject(error);
      },
    ] as unknown as (response: any) => any,
  ],
};
